import { clsx } from 'clsx';
import { AriaAttributes, AriaRole } from 'react';

import type { DallasSpacing } from '../types/spacing';
import type { DallasFlexAlign } from '../types/flex-align';
import type { DallasFlexDirection } from '../types/flex-direction';

import styles from './DallasStack.module.scss';

interface Props extends AriaAttributes {
  as?: 'div' | 'ul' | 'li' | 'header' | 'label' | 'main' | 'nav';
  className?: string;
  children: React.ReactNode;
  role?: AriaRole;
  flex?: boolean;
  'compact-flex'?: boolean;
  'extended-flex'?: boolean;
  gap?: DallasSpacing;
  'compact-gap'?: DallasSpacing;
  'extended-gap'?: DallasSpacing;
  direction?: DallasFlexDirection;
  'compact-direction'?: DallasFlexDirection;
  'extended-direction'?: DallasFlexDirection;
  alignVertical?: DallasFlexAlign;
  'compact-alignVertical'?: DallasFlexAlign;
  'extended-alignVertical'?: DallasFlexAlign;
  alignHorizontal?: DallasFlexAlign;
  'compact-alignHorizontal'?: DallasFlexAlign;
  'extended-alignHorizontal'?: DallasFlexAlign;
  padding?: DallasSpacing;
  'compact-padding'?: DallasSpacing;
  'extended-padding'?: DallasSpacing;
  paddingVertical?: DallasSpacing;
  'compact-paddingVertical'?: DallasSpacing;
  'extended-paddingVertical'?: DallasSpacing;
  paddingTop?: DallasSpacing;
  'compact-paddingTop'?: DallasSpacing;
  'extended-paddingTop'?: DallasSpacing;
  paddingBottom?: DallasSpacing;
  'compact-paddingBottom'?: DallasSpacing;
  'extended-paddingBottom'?: DallasSpacing;
  paddingHorizontal?: DallasSpacing;
  'compact-paddingHorizontal'?: DallasSpacing;
  'extended-paddingHorizontal'?: DallasSpacing;
  paddingStart?: DallasSpacing;
  'compact-paddingStart'?: DallasSpacing;
  'extended-paddingStart'?: DallasSpacing;
  paddingEnd?: DallasSpacing;
  'compact-paddingEnd'?: DallasSpacing;
  'extended-paddingEnd'?: DallasSpacing;
}

export default function DallasStack({
  children,
  className,
  as: Root = 'div',
  role,
  flex,
  'compact-flex': flexCompact,
  'extended-flex': flexExtended,
  gap = 'lg',
  'compact-gap': gapCompact,
  'extended-gap': gapExtended,
  direction = 'column',
  'compact-direction': directionCompact = direction,
  'extended-direction': directionExtended = direction,
  alignVertical = 'start',
  'compact-alignVertical': alignVerticalCompact,
  'extended-alignVertical': alignVerticalExtended,
  alignHorizontal: alignHorizontal = 'start',
  'compact-alignHorizontal': alignHorizontalCompact,
  'extended-alignHorizontal': alignHorizontalExtended,
  padding,
  'compact-padding': paddingCompact,
  'extended-padding': paddingExtended,
  paddingVertical,
  'compact-paddingVertical': paddingVerticalCompact,
  'extended-paddingVertical': paddingVerticalExtended,
  paddingTop,
  'compact-paddingTop': paddingTopCompact,
  'extended-paddingTop': paddingTopExtended,
  paddingBottom,
  'compact-paddingBottom': paddingBottomCompact,
  'extended-paddingBottom': paddingBottomExtended,
  paddingHorizontal: paddingHorizontal,
  'compact-paddingHorizontal': paddingHorizontalCompact,
  'extended-paddingHorizontal': paddingHorizontalExtended,
  paddingStart,
  'compact-paddingStart': paddingStartCompact,
  'extended-paddingStart': paddingStartExtended,
  paddingEnd,
  'compact-paddingEnd': paddingEndCompact,
  'extended-paddingEnd': paddingEndExtended,
  ...ariaProps
}: Props) {
  return (
    <Root
      role={role}
      className={clsx(
        className,
        styles.root,
        flex && styles.flex,
        flexCompact && styles['compact-flex'],
        flexExtended && styles['extended-flex'],
        styles[`gap-${gap}`],
        gapCompact && styles[`compact-gap-${gapCompact}`],
        gapExtended && styles[`extended-gap-${gapExtended}`],
        styles[`direction-${direction}`],
        directionCompact && styles[`compact-direction-${directionCompact}`],
        directionExtended && styles[`extended-direction-${directionExtended}`],
        styles[`direction-${direction}-alignVertical-${alignVertical}`],
        directionCompact &&
          alignVerticalCompact &&
          styles[
            `compact-direction-${directionCompact}-alignVertical-${alignVerticalCompact}`
          ],
        directionExtended &&
          alignVerticalExtended &&
          styles[
            `extended-direction-${directionExtended}-alignVertical-${alignVerticalExtended}`
          ],
        styles[`direction-${direction}-alignHorizontal-${alignHorizontal}`],
        directionCompact &&
          alignHorizontalCompact &&
          styles[
            `compact-direction-${directionCompact}-alignHorizontal-${alignHorizontalCompact}`
          ],
        directionExtended &&
          alignHorizontalExtended &&
          styles[
            `extended-direction-${directionExtended}-alignHorizontal-${alignHorizontalExtended}`
          ],
        padding && styles[`padding-${padding}`],
        paddingCompact && styles[`compact-padding-${paddingCompact}`],
        paddingExtended && styles[`extended-padding-${paddingExtended}`],
        paddingVertical && styles[`paddingVertical-${paddingVertical}`],
        paddingVerticalCompact &&
          styles[`compact-paddingVertical-${paddingVerticalCompact}`],
        paddingVerticalExtended &&
          styles[`extended-paddingVertical-${paddingVerticalExtended}`],
        paddingTop && styles[`paddingTop-${paddingTop}`],
        paddingTopCompact && styles[`compact-paddingTop-${paddingTopCompact}`],
        paddingTopExtended &&
          styles[`extended-paddingTop-${paddingTopExtended}`],
        paddingBottom && styles[`paddingBottom-${paddingBottom}`],
        paddingBottomCompact &&
          styles[`compact-paddingBottom-${paddingBottomCompact}`],
        paddingBottomExtended &&
          styles[`extended-paddingBottom-${paddingBottomExtended}`],
        paddingHorizontal && styles[`paddingHorizontal-${paddingHorizontal}`],
        paddingHorizontalCompact &&
          styles[`compact-paddingHorizontal-${paddingHorizontalCompact}`],
        paddingHorizontalExtended &&
          styles[`extended-paddingHorizontal-${paddingHorizontalExtended}`],
        paddingStart && styles[`paddingStart-${paddingStart}`],
        paddingStartCompact &&
          styles[`compact-paddingStart-${paddingStartCompact}`],
        paddingStartExtended &&
          styles[`extended-paddingStart-${paddingStartExtended}`],
        paddingEnd && styles[`paddingEnd-${paddingEnd}`],
        paddingEndCompact && styles[`compact-paddingEnd-${paddingEndCompact}`],
        paddingEndExtended &&
          styles[`extended-paddingEnd-${paddingEndExtended}`],
      )}
      {...ariaProps}
    >
      {children}
    </Root>
  );
}
